import React from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useLaunchParams } from '@telegram-apps/sdk-react';
import { Header } from '@/components/Header';
import { ReferralCodeEntry } from '@/components/ReferralCodeEntry';
import { SuccessDialog } from '@/components/SuccessDialog';
import { useReferrals } from '@/providers/ReferralsProvider';
import { useQueryClient } from '@tanstack/react-query';
import { referralKeys } from '@/hooks/useReferrals';

export const ReferralEntryPage: React.FC = () => {
  const launchParams = useLaunchParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const userId = launchParams.initData?.user?.id?.toString();
  const { 
    loading, 
    error, 
    showSuccessDialog, 
    setShowSuccessDialog, 
    handleReferralCodeSubmit, 
    handleSkipReferral 
  } = useReferrals();

  // Extract referral code from start parameter
  const startParam = launchParams.initData?.startParam;
  const urlStartParam = searchParams.get('start_param');
  const referralCodeFromStart = startParam || urlStartParam || '';

  const onSubmit = async (code: string) => {
    try {
      await handleReferralCodeSubmit(code);
      // Provider will automatically redirect based on user status
    } catch (err) {
      // Error already handled in provider
    }
  };

  const onSkip = async () => {
    try {
      await handleSkipReferral();
      // Provider will automatically redirect based on user status
    } catch (err) {
      // Error already handled in provider
    }
  };

  const handleSuccessDialogContinue = () => {
    setShowSuccessDialog(false);
    queryClient.invalidateQueries({
      queryKey: referralKeys.stats(userId || ''),
    });
    queryClient.invalidateQueries({
      queryKey: referralKeys.isReferred(userId || ''),
    });
    queryClient.invalidateQueries({
      queryKey: referralKeys.whitelistStatus(userId || ''),
    });
    navigate('/referrals');
    // The provider will handle navigation based on user status
  };

  return (
    <div className="min-h-screen">
      <Header backVisible={false} subtitle="Enter Referral Code" />
      <ReferralCodeEntry
        onCodeSubmit={onSubmit}
        loading={loading}
        error={error || undefined}
        initialCode={referralCodeFromStart}
        onSkip={onSkip}
      />
      <SuccessDialog
        open={showSuccessDialog}
        onOpenChange={setShowSuccessDialog}
        onContinue={handleSuccessDialogContinue}
      />
    </div>
  );
};

